import type { GetAvailableClassResponse } from "@/openapi/generated";
import {
  getScheduleEntryWallClock,
  normalizeMakeupTimeOnly,
  scheduleEntryEndTime,
  scheduleEntryStartTime,
  scheduleInstantTimeOnly,
} from "./scheduleSlotDisplay";
import type { ScheduleInstantSlot } from "./scheduleSlotTiming";

/** Backend rejects midnight-only placeholders used when a class has no real start time. */
export function isValidMakeupTimeOnly(
  value: string | null | undefined,
): boolean {
  if (!value?.trim()) return false;
  return normalizeMakeupTimeOnly(value) !== "00:00:00";
}

export function hasValidClassMeetingTimes(
  startTime: string | null | undefined,
  endTime: string | null | undefined,
): boolean {
  return isValidMakeupTimeOnly(startTime) && isValidMakeupTimeOnly(endTime);
}

/** True when the class has at least one configured meeting time parents can request. */
export function hasMakeupEligibleClassSchedule(
  cls: GetAvailableClassResponse,
): boolean {
  for (const meeting of cls.classDays ?? []) {
    if (hasValidClassMeetingTimes(meeting.startTime, meeting.endTime)) {
      return true;
    }
  }

  return hasValidClassMeetingTimes(cls.classStartTime, cls.classEndTime);
}

export function isBookableScheduleSlot(
  entry: ScheduleInstantSlot,
  timeZone?: string | null,
): boolean {
  const wall = getScheduleEntryWallClock(entry);
  if (wall) {
    return (
      isValidMakeupTimeOnly(wall.startTime) &&
      isValidMakeupTimeOnly(wall.endTime) &&
      wall.endTime > wall.startTime
    );
  }

  const start = entry.start instanceof Date ? entry.start : new Date(entry.start);
  const end = entry.end instanceof Date ? entry.end : new Date(entry.end);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
  if (end.getTime() <= start.getTime()) return false;

  return (
    isValidMakeupTimeOnly(scheduleInstantTimeOnly(start, timeZone)) &&
    isValidMakeupTimeOnly(scheduleInstantTimeOnly(end, timeZone))
  );
}

export function filterBookableScheduleSlots<T extends ScheduleInstantSlot>(
  entries: T[],
  timeZone?: string | null,
): T[] {
  return entries.filter((entry) => isBookableScheduleSlot(entry, timeZone));
}
